import { h, VNode } from "snabbdom";

import { Suit, Card, slotIndex, Vec2D, Grid, RenderGameState } from "./types";
import { Player } from "./hand";
import { Globals } from "./globals";

const MIN_WIDTH: number = 320;
const MIN_HEIGHT: number = MIN_WIDTH * 1.5;
const GRID: Grid = {
  opponentHand: { x: 1, y: 1 },
  opponentDeck: { x: 6, y: 1 },
  leftPile: { x: 2, y: 3.5 },
  rightPile: { x: 3, y: 3.5 },
  sidePile: { x: 6, y: 3.5 },
  playerHand: { x: 1, y: 6 },
  playerDeck: { x: 6, y: 6 },
};

// --- VIEW ---
// should be a function in s (and the current tick). This means that it is soley
// dependent on the current state and can be deterministicly recreated by feeding
// it the same state
export function view(s: RenderGameState): VNode {
  //console.log("rendering: " + JSON.stringify(s));
  if (s.winner) {
    return h("div.winner-modal", [
      h("h1", s.winner ? "You won!" : "You lost!"),
      h(
        "button",
        {
          on: {
            click: () => {
              console.log("TODO: figure out what to do here");
            },
          },
        },
        "Play Again",
      ),
    ]);
  }

  const leftTopCard: number | undefined = s.leftPile.peek();
  const rightTopCard: number | undefined = s.rightPile.peek();

  // Source - https://stackoverflow.com/a/8876069
  let vwidth: number = Math.max(
    document.documentElement.clientWidth || 0,
    window.innerWidth || 0,
    MIN_WIDTH,
  );
  let vheight: number = Math.max(
    document.documentElement.clientHeight || 0,
    window.innerHeight || 0,
    MIN_HEIGHT,
  );

  //console.log(`original width: ${vwidth}, height: ${vheight}`);
  if (vwidth * 1.5 < vheight) {
    vheight = vwidth * 1.5;
  } else {
    vwidth = (vheight / 3) * 2;
  }
  //console.log(`adjusted width: ${vwidth}, height: ${vheight}`);
  const widthSlot = vwidth / 8;
  const heightSlot = vheight / 8;

  function renderCard(
    pos: Vec2D,
    card: number,
    isSelected: boolean,
    onClick?: () => void,
  ): VNode {
    const isRed =
      Card.suit(card) === Suit.Hearts || Card.suit(card) === Suit.Diamonds;
    const displayRank =
      Card.rank(card) === 1
        ? "A"
        : Card.rank(card) === 11
          ? "J"
          : Card.rank(card) === 12
            ? "Q"
            : Card.rank(card) === 13
              ? "K"
              : Card.rank(card);

    return h(
      "card.card",
      {
        class: { red: isRed, black: !isRed, selected: isSelected },
        style: {
          width: `${widthSlot - 10}px`,
          height: `${heightSlot - 15}px`,
          transform: `translate(${pos.x * widthSlot}px, ${pos.y * heightSlot}px)`,
        },
        on: onClick ? { click: onClick } : {},
      },
      [
        h("span", `${displayRank}`),
        h("span", { style: { fontSize: "2rem" } }, Card.suitToString(card)),
      ],
    );
  }

  function renderHandSlot(
    pos: Vec2D,
    reversed: boolean,
    slot: number[],
    isSelected: boolean,
    onClick?: () => void,
  ): VNode[] {
    const shiftFactor = 0.5 * (reversed ? -1 : 1);
    return slot.map((c, i) =>
      renderCard(
        { x: pos.x, y: pos.y + shiftFactor * i },
        c,
        i === slot.length - 1 && isSelected,
        i === slot.length - 1 && onClick ? onClick : undefined,
      ),
    );
  }

  function renderDeck(
    pos: Vec2D,
    count: number,
    onDeckClick: () => void,
  ): VNode {
    return h(
      "deck.deck",
      {
        on: { click: onDeckClick },
        style: {
          width: `${widthSlot}px`,
          height: `${heightSlot}px`,
          transform: `translate(${pos.x * widthSlot}px, ${pos.y * heightSlot}px)`,
        },
      },
      [
        h(
          "span",
          {
            style: {
              color: "white",
              fontWeight: "bold",
            },
          },
          `${count}`,
        ),
      ],
    );
  }

  // renderes this hand, takes a callback to handle the user clicking on the card
  function renderHand(
    pos: Vec2D,
    reversed: boolean,
    cards: Player,
    onSlotClick: (i: slotIndex) => void,
  ): VNode[] {
    const tmp: VNode[] = [];
    const selectedSlot: slotIndex | undefined =
      cards.state.kind === "selected" ? cards.state.slot : undefined;
    const nodes = tmp.concat(
      ...cards.hand.map((slot, i) =>
        renderHandSlot(
          { x: pos.x + i, y: pos.y },
          reversed,
          slot,
          (i as slotIndex) === selectedSlot,
          () => onSlotClick(i as slotIndex),
        ),
      ),
    );
    return nodes;
  }

  // renders the moving card, if there exists one
  function renderMovingCard(
    basePos: Vec2D,
    reversed: boolean,
    cards: Player,
  ): VNode | null {
    if (cards.state.kind !== "playing") {
      return null;
    }
    const dstPilePos: Vec2D =
      cards.state.dstPile === 5 ? GRID.leftPile : GRID.rightPile;

    const shiftFactor: number = 0.5 * (reversed ? -1 : 1);
    const relativeSlot: number = cards.state.srcSlot % 8;
    const relativeSlotPos: number = cards.hand[relativeSlot].length - 1;

    const slotPos: Vec2D = {
      x: basePos.x + relativeSlot,
      y: basePos.y + shiftFactor * relativeSlotPos,
    };
    const dx =
      (dstPilePos.x - slotPos.x) / Globals.config.client.move_duration_ticks;
    const dy =
      (dstPilePos.y - slotPos.y) / Globals.config.client.move_duration_ticks;

    const pos: Vec2D = {
      x: slotPos.x + dx * (Globals.tick - cards.state.startTick + 1), // +1 as card is already in position
      y: slotPos.y + dy * (Globals.tick - cards.state.startTick + 1),
    };
    return renderCard(pos, cards.state.card, false, () => {});
  }

  // Actually render the canvas
  return h("div.game-table", [
    h(
      "table.canvas",
      { style: { width: `${vwidth}px`, height: `${vheight}px` } },
      [
        // 1. Opponent Area (Top)
        ...renderHand(GRID.opponentHand, true, s.opponent, () => {}), // opponent cards are non clickable
        renderDeck(GRID.opponentDeck, s.opponent.deck.length, () => {}),

        // 2. Middle Area (Piles)
        h("div.middle-area", [
          h("button.stress-btn", { on: { click: s.onStressClick } }, "STRESS!"),
        ]),

        // middle piles

        leftTopCard !== undefined
          ? renderCard(GRID.leftPile, leftTopCard, false, () =>
              s.onPileClick(5),
            )
          : null,
        rightTopCard !== undefined
          ? renderCard(GRID.rightPile, rightTopCard, false, () =>
              s.onPileClick(6),
            )
          : null,
        // side pile
        renderDeck(GRID.sidePile, s.sidePile.length, () => {}),

        // 3. Player Area (Bottom)
        ...renderHand(GRID.playerHand, false, s.player, s.onSlotClick),
        renderDeck(GRID.playerDeck, s.player.deck.length, () =>
          s.onUncoverClick(s.player),
        ),
        renderMovingCard(GRID.playerHand, false, s.player),
        renderMovingCard(GRID.opponentHand, true, s.opponent),
      ],
    ),
  ]);
}
